import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAbroadCounselingServices,
  updateAbroadCounselingService,
  deleteAbroadCounselingService,
} from "../../../services/user.services";
import {
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

function AbroadTable({
  services,
  saveService,
  coreServiceId,
  setServices,
  deleteService,
}) {
  const [editId, setEditId] = useState(null);
  const [editableData, setEditableData] = useState({});
  const [expandedRows, setExpandedRows] = useState(new Set());

  const dispatch = useDispatch();

  // Toggle Edit Mode
  const toggleEdit = (service) => {
    setEditId(service.id);
    setEditableData({
      id: service.id,
      service_name: service.service_name,
      country: service.country,
      level_of_study: service.level_of_study,
      price: service.price,
    });
  };

  const cancelEdit = (id) => {
    if (services.find((service) => service.id === id)?.isNew) {
      setServices((prev) => prev.filter((service) => service.id !== id));
    }
    setEditId(null);
    setEditableData({});
  };

  // Handle input changes in edit mode
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditableData({ ...editableData, [name]: value });
  };

  // Save changes after edit
  const saveEdit = async () => {
    try {
      const updatedData = {
        id: editId,
        service_name: editableData.service_name,
        country: editableData.country,
        price: editableData.price,
        level_of_study: editableData.level_of_study,
        coreservices_id: coreServiceId,
      };
      const doesExist = services.find(
        (service) => service.id === updatedData.id
      );
      if (!doesExist) {
        await saveService({
          id: editId,
          service_name: editableData.service_name,
          country: editableData.country,
          price: editableData.price,
          level_of_study: editableData.level_of_study,
          coreservices_id: coreServiceId,
        });
        setServices((prev) =>
          prev.map((item) =>
            item.id === editId
              ? { ...item, ...editableData, isNew: false }
              : item
          )
        );
      } else {
        await updateAbroadCounselingService(editId, updatedData);
        setServices((prev) =>
          prev.map((item) =>
            item.id === editId ? { ...item, ...updatedData } : item
          )
        );
      }

      setEditId(null);
      setEditableData({});
    } catch (error) {
      console.error(
        "Error updating the service:",
        error.response?.data || error.message
      );
    }
  };

  // Delete a service
  const deleteRow = async (id) => {
    try {
      await deleteService(id);
      const filteredServices = services.filter((service) => service.id !== id);
      setServices(filteredServices);
    } catch (error) {
      console.error("Error deleting the service:", error);
    }
  };

  const toggleSelect = (id) => {
    setServices(
      services.map((service) =>
        service.id === id
          ? { ...service, selected: !service.selected }
          : service
      )
    );
  };

  // Toggle expanded state for mobile cards
  const toggleExpanded = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  // Render input field
  const renderInput = (name, value, placeholder, type = "text") => (
    <input
      type={type}
      name={name}
      value={value}
      onChange={handleEditChange}
      placeholder={placeholder}
      className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
    />
  );

  // Mobile Card Component
  const MobileCard = ({ service }) => {
    const isEditing = editId === service.id || service.isNew;
    const isExpanded = expandedRows.has(service.id);

    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-3">
        {/* Card Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                className="cursor-pointer"
                checked={service.selected}
                onChange={() => toggleSelect(service.id)}
              />
              <div>
                <h3 className="font-medium text-gray-900">
                  {isEditing
                    ? renderInput(
                        "service_name",
                        editableData.service_name ?? service.service_name,
                        "Service Name"
                      )
                    : service.service_name || "Untitled Service"}
                </h3>
                <p className="text-sm text-gray-500">ID: {service.id}</p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* Action Buttons */}
              {isEditing ? (
                <>
                  <button
                    onClick={saveEdit}
                    className="text-green-600 hover:text-green-800 p-2"
                    title="Save"
                  >
                    <FaSave />
                  </button>
                  <button
                    onClick={() => cancelEdit(service.id)}
                    className="text-gray-600 hover:text-gray-800 p-2"
                    title="Cancel"
                  >
                    <FaTimes />
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => toggleEdit(service)}
                    className="text-blue-600 hover:text-blue-800 p-2"
                    title="Edit"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => deleteRow(service.id)}
                    className="text-red-600 hover:text-red-800 p-2"
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </>
              )}

              {/* Expand/Collapse Button */}
              <button
                onClick={() => toggleExpanded(service.id)}
                className="text-gray-400 hover:text-gray-600 p-2"
                title={isExpanded ? "Collapse" : "Expand"}
              >
                {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
              </button>
            </div>
          </div>
        </div>

        {/* Expandable Content */}
        {isExpanded && (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                {isEditing ? (
                  renderInput(
                    "country",
                    editableData.country ?? service.country,
                    "Country"
                  )
                ) : (
                  <p className="text-gray-900">{service.country || "N/A"}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Level of Study
                </label>
                {isEditing ? (
                  renderInput(
                    "level_of_study",
                    editableData.level_of_study ?? service.level_of_study,
                    "Level of Study"
                  )
                ) : (
                  <p className="text-gray-900">
                    {service.level_of_study || "N/A"}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price
                </label>
                {isEditing ? (
                  renderInput(
                    "price",
                    editableData.price ?? service.price,
                    "Price"
                  )
                ) : (
                  <p className="text-blue-600 font-medium">
                    {service.price || "N/A"}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      {/* Mobile View */}
      <div className="block md:hidden">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Services</h3>
            <p className="text-sm text-gray-500">{services.length} items</p>
          </div>

          {services.length > 0 ? (
            services.map((service) => (
              <MobileCard key={service.id} service={service} />
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No services available</p>
            </div>
          )}
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block">
        <div className="container mx-auto p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal">
                  <th className="py-3 px-4 text-center">
                    <input type="checkbox" className="cursor-pointer" />
                  </th>
                  <th className="py-3 px-4 text-left">SL</th>
                  <th className="py-3 px-4 text-left">Name of the Services</th>
                  <th className="py-3 px-4 text-left">Country</th>
                  <th className="py-3 px-4 text-left">Level of Study</th>
                  <th className="py-3 px-4 text-left">Price</th>
                  <th className="py-3 px-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {services.length > 0 ? (
                  services.map((service) => (
                    <tr
                      key={service.id}
                      className="border-b border-gray-300 hover:bg-gray-50"
                    >
                      <td className="py-3 px-4 text-center">
                        <input
                          type="checkbox"
                          className="cursor-pointer"
                          checked={service.selected}
                          onChange={() => toggleSelect(service.id)}
                        />
                      </td>
                      <td className="py-3 px-4 text-center">{service.id}</td>
                      <td className="py-3 px-4">
                        {editId === service.id || service.isNew ? (
                          <input
                            type="text"
                            name="service_name"
                            value={
                              editableData.service_name ?? service.service_name
                            }
                            onChange={handleEditChange}
                            className="border border-gray-300 px-2 py-1 rounded w-full"
                            placeholder="Service Name"
                          />
                        ) : (
                          service.service_name
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {editId === service.id || service.isNew ? (
                          <input
                            type="text"
                            name="country"
                            value={editableData.country ?? service.country}
                            onChange={handleEditChange}
                            className="border border-gray-300 px-2 py-1 rounded w-full"
                            placeholder="Country"
                          />
                        ) : (
                          service.country
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {editId === service.id || service.isNew ? (
                          <input
                            type="text"
                            name="level_of_study"
                            value={
                              editableData.level_of_study ??
                              service.level_of_study
                            }
                            onChange={handleEditChange}
                            className="border border-gray-300 px-2 py-1 rounded w-full"
                            placeholder="Level of Study"
                          />
                        ) : (
                          service.level_of_study
                        )}
                      </td>
                      <td className="py-3 px-4 text-blue-600">
                        {editId === service.id || service.isNew ? (
                          <input
                            type="text"
                            name="price"
                            value={editableData.price ?? service.price}
                            onChange={handleEditChange}
                            className="border border-gray-300 px-2 py-1 rounded w-full"
                            placeholder="Price"
                          />
                        ) : (
                          service.price
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex space-x-2">
                          {editId === service.id || service.isNew ? (
                            <>
                              <button
                                onClick={saveEdit}
                                className="text-green-600 hover:text-green-800 p-1"
                                title="Save"
                              >
                                <FaSave />
                              </button>
                              <button
                                onClick={() => cancelEdit(service.id)}
                                className="text-gray-600 hover:text-gray-800 p-1"
                                title="Cancel"
                              >
                                <FaTimes />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => toggleEdit(service)}
                                className="text-blue-600 hover:text-blue-800 p-1"
                                title="Edit"
                              >
                                <FaEdit />
                              </button>
                              <button
                                onClick={() => deleteRow(service.id)}
                                className="text-red-600 hover:text-red-800 p-1"
                                title="Delete"
                              >
                                <FaTrash />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="py-8 text-center text-gray-500">
                      No services available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AbroadTable;
